import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Import images
import slide1 from '../assets/Images/WhatsApp Image 2025-04-27 at 11.19.32 (1).jpeg';
import slide2 from '../assets/Images/WhatsApp Image 2025-04-27 at 11.19.32 (2).jpeg';
import slide3 from '../assets/Images/WhatsApp Image 2025-04-27 at 11.19.32 (5).jpeg';
import slide4 from '../assets/Images/WhatsApp Image 2025-04-27 at 11.16.41.jpeg';
import slide5 from '../assets/Images/logo.jpeg';

interface Slide {
  id: number;
  image: string;
  titleKey: string;
  descriptionKey: string;
  link: string;
}

const slides: Slide[] = [
  {
    id: 1,
    image: slide1,
    titleKey: 'hero.slides.slide1.title',
    descriptionKey: 'hero.slides.slide1.description',
    link: '/gallery'
  },
  {
    id: 2,
    image: slide2,
    titleKey: 'hero.slides.slide2.title',
    descriptionKey: 'hero.slides.slide2.description',
    link: '/gallery'
  },
  {
    id: 3,
    image: slide3,
    titleKey: 'hero.slides.slide3.title',
    descriptionKey: 'hero.slides.slide3.description',
    link: '/gallery'
  },
  {
    id: 4,
    image: slide4,
    titleKey: 'hero.slides.slide4.title',
    descriptionKey: 'hero.slides.slide4.description',
    link: '/gallery'
  },
  {
    id: 5,
    image: slide5,
    titleKey: 'hero.slides.slide5.title',
    descriptionKey: 'hero.slides.slide5.description',
    link: '/gallery'
  }
];

const HeroSlider: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    // Preload images
    const loadImages = async () => {
      try {
        await Promise.all(
          slides.map(
            (slide) =>
              new Promise((resolve, reject) => {
                const img = new Image();
                img.src = slide.image;
                img.onload = resolve;
                img.onerror = reject;
              })
          )
        );
        setImagesLoaded(true);
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };

    loadImages();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  if (!imagesLoaded) {
    return (
      <div className="relative h-[calc(100vh-4rem)] w-full bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="relative h-[calc(100vh-4rem)] w-full overflow-hidden">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={t(slide.titleKey)}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          <div className="relative h-full flex items-center justify-center text-center px-4">
            <div className="max-w-3xl">
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
                {t(slide.titleKey)}
              </h2>
              <p className="text-xl md:text-2xl text-white mb-8">
                {t(slide.descriptionKey)}
              </p>
              <Link
                to={slide.link}
                className="inline-block bg-white text-gray-900 px-8 py-3 rounded-md text-lg font-medium hover:bg-gray-100 transition-colors duration-200"
              >
                {t('common.learnMore')}
              </Link>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-30 hover:bg-opacity-50 text-white p-2 rounded-full transition-all duration-200"
        aria-label={t('common.previous')}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-30 hover:bg-opacity-50 text-white p-2 rounded-full transition-all duration-200"
        aria-label={t('common.next')}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              index === currentSlide
                ? 'bg-white scale-125'
                : 'bg-white bg-opacity-50 hover:bg-opacity-75'
            }`}
            aria-label={`${t('common.goToSlide')} ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroSlider; 